import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { workspacePath } from '@wrapforce/shared/api-client';
import { ApiError } from '@wrapforce/shared/errors';
import { api } from '@/lib/api';
import { takeDirectoryClaim } from '@/lib/directory-claim';

interface DirectoryClaimLinkResult {
  shopName: string;
  shopSlug: string;
  convertedLeads: number;
}

// Links a pending Car Wrap Shops directory claim (see docs/DIRECTORY_SPEC.md)
// to the given workspace, if a claim token is stashed in localStorage.
// takeDirectoryClaim() clears the token as soon as it is read, so this is a
// no-op on every call after the first - safe to call every time a workspace
// becomes active (see AppShell.tsx for why that is the one call site).
export function useLinkPendingDirectoryClaim(workspaceId: string | null) {
  const { t } = useTranslation('workspace');
  const started = useRef(false);

  useEffect(() => {
    if (!workspaceId || started.current) return;
    const token = takeDirectoryClaim();
    if (!token) return;
    started.current = true;

    api
      .post<DirectoryClaimLinkResult>(workspacePath(workspaceId, '/directory/claims/link'), { token })
      .then((result) => {
        toast.success(t('toast.directoryClaimImported', { count: result.convertedLeads, shopName: result.shopName }));
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 409) {
          toast.error(t('toast.directoryClaimAlreadyLinked'));
        }
        // 404 (unknown/expired token) and any other failure are left silent -
        // the token is already gone from storage, so there's nothing to retry.
      });
  }, [workspaceId, t]);
}
